import React from "react";
import { Typography, Box, Stack, Chip } from "@mui/material";
import { motion } from "framer-motion";

const stats = [
    { value: "$5.5M", label: "Annual savings delivered" },
    { value: "400+", label: "Sites served" },
    { value: "96%", label: "Onboarding time cut" },
    { value: "2×", label: "Extra Miler award" },
];

const tags = [
    "Agent Platforms", "LLM Infrastructure", "Distributed Systems",
    "MLOps", "AWS", "Python", "Knowledge Graphs", "Apache Iceberg",
];

const About: React.FC = () => (
    <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <Box sx={{ mb: 1 }}>
            <Typography
                sx={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "11px",
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: "primary.main",
                    mb: 2,
                }}
            >
                Senior Data &amp; AI Engineer · L6
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontWeight: 300,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                    mb: 0.5,
                    fontSize: { xs: "2.2rem", sm: "3rem" },
                }}
            >
                Gautham Rajendiran
            </Typography>

            <Typography
                sx={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "12px",
                    color: "text.secondary",
                    letterSpacing: "0.05em",
                    mb: 3,
                }}
            >
                Amazon AMZL · Vancouver, BC
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    color: "text.secondary",
                    lineHeight: 1.8,
                    maxWidth: 620,
                    mb: 1.5,
                    fontWeight: 300,
                }}
            >
                10 years building production AI and data systems at Amazon and PepsiCo. Currently the architect behind AskOrbit, a conversational AI agent serving 400+ AMZL sites, and the multi-agent orchestration framework that became the standard across Orbit AI systems.
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    color: "text.secondary",
                    lineHeight: 1.8,
                    maxWidth: 620,
                    mb: 4,
                    fontWeight: 300,
                }}
            >
                I identify pain points, build the architecture, write the design docs, and own delivery end to end, including oncall. Looking for Senior SWE roles in AI-focused areas.
            </Typography>
        </Box>

        {/* Stats */}
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                gap: "1px",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
                mb: 4,
            }}
        >
            {stats.map((s) => (
                <Box
                    key={s.label}
                    sx={{
                        bgcolor: "background.paper",
                        p: 2.5,
                        transition: "background 0.2s",
                        "&:hover": { bgcolor: "#181c22" },
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "'Fraunces', serif",
                            fontWeight: 600,
                            fontSize: "2rem",
                            letterSpacing: "-0.03em",
                            color: "primary.main",
                            lineHeight: 1,
                            mb: 0.5,
                        }}
                    >
                        {s.value}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                        {s.label}
                    </Typography>
                </Box>
            ))}
        </Box>

        {/* Tags */}
        <Stack direction="row" flexWrap="wrap" gap={1}>
            {tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
            ))}
        </Stack>
    </motion.div>
);

export default About;
